import { useEffect, useState, useCallback } from 'react';
import { useGenerateCode } from '@/entities/content';
import type { GenerateCodeRequest } from '@/entities/content';

type UseFormCodeGenerationParams = {
  /** Current name value (can be a getter function) */
  getName: () => string;
  /** Current code value (can be a getter function) */
  getCode: () => string;
  /** Callback to set code value */
  setCode: (code: string) => void;
  /** Entity type for code generation */
  entityType: GenerateCodeRequest['entityType'];
  /** Whether we're in edit mode (code generation disabled in edit mode) */
  isEdit: boolean;
  /** Minimum name length to trigger code generation (default: 5) */
  minNameLength?: number;
};

type UseFormCodeGenerationResult = {
  /** Whether code field should be locked */
  isCodeLocked: boolean;
  /** Whether code generation is in progress */
  isGeneratingCode: boolean;
  /** Handler for name field blur event */
  handleNameBlur: () => Promise<void>;
};

/**
 * Hook for managing code generation in forms
 *
 * Handles automatic code generation when name field loses focus:
 * - Locks code field initially when creating (not editing)
 * - Unlocks code field when name has content
 * - Generates code automatically if name > minNameLength and code is empty
 * - Locks code field during generation
 * - Sets generated code on success
 * - Unlocks code field on error
 *
 * @example
 * ```tsx
 * const form = useForm<FormValues>({ ... });
 * const { isCodeLocked, isGeneratingCode, handleNameBlur } = useFormCodeGeneration({
 *   getName: () => form.getValues('name'),
 *   getCode: () => form.getValues('code'),
 *   setCode: (code) => form.setValue('code', code),
 *   entityType: 'attribute',
 *   isEdit: !!initial,
 * });
 * ```
 */
export const useFormCodeGeneration = (params: UseFormCodeGenerationParams): UseFormCodeGenerationResult => {
  const { getName, getCode, setCode, entityType, isEdit, minNameLength = 5 } = params;

  const { mutateAsync: generateCode, isPending: isGeneratingCode } = useGenerateCode();

  // State for code field lock during creation
  const [isCodeLocked, setIsCodeLocked] = useState(!isEdit);

  // Reset code lock state when switching between edit/create modes
  useEffect(() => {
    if (isEdit) {
      setIsCodeLocked(false);
    } else {
      setIsCodeLocked(true);
    }
  }, [isEdit]);

  // Handle name field blur - unlock code and generate if needed
  const handleNameBlur = useCallback(async () => {
    const name = getName();
    const code = getCode();

    // If name has content, unlock code field
    if (name.trim().length > 0) {
      setIsCodeLocked(false);
    }

    // Generate code if name > minNameLength, code is empty, and we're creating (not editing)
    if (name.trim().length >= minNameLength && !code && !isEdit) {
      try {
        setIsCodeLocked(true); // Lock during generation
        const response = await generateCode({
          name: name.trim(),
          entityType,
        });
        setCode(response.code);
        setIsCodeLocked(false); // Unlock after successful generation
      } catch {
        // On error, just unlock the field
        setIsCodeLocked(false);
      }
    }
  }, [getName, getCode, setCode, entityType, isEdit, minNameLength, generateCode]);

  return {
    isCodeLocked: isCodeLocked || isGeneratingCode,
    isGeneratingCode,
    handleNameBlur,
  };
};
